import { ContentType, TreeViewNode } from 'models';
import { getContentTypeContext, getContentType } from './getContentType';

/**
 * Filters a tree to only the leaf nodes whose content-type type is in
 * selectedTypes, keeping folders that still have at least one matching
 * descendant. An empty selection means "no filter" — the tree is returned
 * unchanged. Unlike filterTreeItems, a folder is never kept on its own
 * label match — folders have no content type of their own.
 */
export const filterTreeByContentType = (
	items: TreeViewNode[],
	selectedTypes: Set<ContentType>
): TreeViewNode[] => {
	if (selectedTypes.size === 0) return items;

	// Read once for the whole pass instead of per-node (getContentType would
	// otherwise re-read composer settings for every leaf in the tree).
	const contentTypeContext = getContentTypeContext();

	const filterNode = (node: TreeViewNode): TreeViewNode | null => {
		const isFolder = !!(node.children && node.children.length > 0);

		if (isFolder) {
			const filteredChildren = node.children!
				.map(filterNode)
				.filter(Boolean) as TreeViewNode[];
			if (!filteredChildren.length) return null;
			// Nothing changed anywhere in this subtree (every child came back
			// as the exact same reference) — return the original node instead
			// of cloning it, so unaffected subtrees stay reference-stable.
			const unchanged =
				filteredChildren.length === node.children!.length &&
				filteredChildren.every((child, i) => child === node.children![i]);
			if (unchanged) return node;
			return { ...node, children: filteredChildren };
		}

		const contentType = getContentType(node, contentTypeContext);
		return contentType && selectedTypes.has(contentType) ? node : null;
	};

	return items.reduce<TreeViewNode[]>((acc, node) => {
		const result = filterNode(node);
		if (result) acc.push(result);
		return acc;
	}, []);
};
